import { FormEvent, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ApiError, type Branch } from "@/lib/api";

export default function BranchesPage() {
  const qc = useQueryClient();
  const branches = useQuery<Branch[]>({
    queryKey: ["md", "branches"],
    queryFn: api.listBranches,
  });
  const companies = useQuery({ queryKey: ["md", "companies"], queryFn: api.listCompanies });
  const locations = useQuery({ queryKey: ["md", "locations"], queryFn: api.listLocations });

  const [name, setName] = useState("");
  const [key, setKey] = useState("");
  const [companyId, setCompanyId] = useState("");
  const [locationId, setLocationId] = useState("");
  const [timezone, setTimezone] = useState("UTC");
  const [error, setError] = useState<string | null>(null);

  const companyById = useMemo(() => {
    const m = new Map<string, string>();
    companies.data?.forEach((c) => m.set(c.id, c.name));
    return m;
  }, [companies.data]);

  const create = useMutation({
    mutationFn: () =>
      api.createBranch({
        name,
        company_id: companyId,
        natural_key: key || null,
        location_id: locationId || null,
        timezone: timezone || "UTC",
      }),
    onSuccess: () => {
      setName("");
      setKey("");
      setLocationId("");
      setError(null);
      qc.invalidateQueries({ queryKey: ["md", "branches"] });
    },
    onError: (err) =>
      setError(err instanceof ApiError ? err.detail ?? err.message : String(err)),
  });

  function submit(e: FormEvent) {
    e.preventDefault();
    if (!name || !companyId) return;
    create.mutate();
  }

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-semibold text-brand-900">Branches</h1>
        <p className="text-sm text-slate-600 mt-1">
          Operational unit. Every branch belongs to exactly one company and
          optionally to a location.
        </p>
      </header>

      <section className="bg-white rounded-lg border shadow-sm p-5">
        <h2 className="font-medium">Add a branch</h2>
        <form onSubmit={submit} className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-3">
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Name"
            required
            className="rounded-md border px-3 py-2 text-sm"
          />
          <select
            value={companyId}
            onChange={(e) => setCompanyId(e.target.value)}
            required
            className="rounded-md border px-3 py-2 text-sm bg-white"
          >
            <option value="">Company…</option>
            {companies.data?.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
          <select
            value={locationId}
            onChange={(e) => setLocationId(e.target.value)}
            className="rounded-md border px-3 py-2 text-sm bg-white"
          >
            <option value="">Location (optional)</option>
            {locations.data?.map((l) => (
              <option key={l.id} value={l.id}>
                {l.name}
              </option>
            ))}
          </select>
          <input
            value={key}
            onChange={(e) => setKey(e.target.value)}
            placeholder="Natural key (optional)"
            className="rounded-md border px-3 py-2 text-sm"
          />
          <input
            value={timezone}
            onChange={(e) => setTimezone(e.target.value)}
            placeholder="Timezone (e.g. Atlantic/Canary)"
            className="rounded-md border px-3 py-2 text-sm"
          />
          <button
            type="submit"
            disabled={create.isPending || !name || !companyId}
            className="bg-brand-600 hover:bg-brand-700 text-white text-sm rounded-md px-3 py-2 disabled:opacity-60"
          >
            {create.isPending ? "Saving…" : "Create"}
          </button>
        </form>
        {error && <p className="mt-2 text-sm text-rose-700">{error}</p>}
      </section>

      <section className="bg-white rounded-lg border shadow-sm p-5">
        <h2 className="font-medium">Existing branches</h2>
        {branches.isLoading && <p className="text-sm text-slate-500 mt-2">Loading…</p>}
        {branches.data && (
          <table className="mt-3 w-full text-sm">
            <thead className="text-slate-500 border-b">
              <tr>
                <th className="text-left py-2">Name</th>
                <th className="text-left py-2">Company</th>
                <th className="text-left py-2">Timezone</th>
                <th className="text-left py-2">Currency</th>
                <th className="text-left py-2">Natural key</th>
              </tr>
            </thead>
            <tbody>
              {branches.data.map((b) => (
                <tr key={b.id} className="border-b last:border-none">
                  <td className="py-2">{b.name}</td>
                  <td className="py-2">{companyById.get(b.company_id) ?? b.company_id}</td>
                  <td className="py-2">{b.timezone}</td>
                  <td className="py-2">{b.default_currency ?? "—"}</td>
                  <td className="py-2 font-mono text-xs">{b.natural_key ?? "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
}
